import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Radio, Search, ChevronDown, ChevronRight, Loader2, RefreshCw, ExternalLink,
  AlertTriangle, Zap, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatCOP } from "../lib/apiClient";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const SCHEMA_FRESH_DAYS = 30;

// Campos que pueden mapearse a inputs del form. Si el campo no existe en
// `available_columns` del dataset, el input se deshabilita.
// keyword (q) siempre está habilitado (es un text search global).
const SUPPORTED_PARAMS = [
  { id: "q", label: "Búsqueda libre (q)", required_field: null, type: "text",
    placeholder: "palabras clave" },
  { id: "valor_min", label: "Valor mínimo", required_field: "value_field", type: "number",
    placeholder: "0" },
  { id: "valor_max", label: "Valor máximo", required_field: "value_field", type: "number",
    placeholder: "1000000000" },
];

/**
 * Panel "Buscar en SECOP en vivo".
 * - Selector de dataset (del registry).
 * - Form dinámico: inputs cuyo `required_field` no existe en `available_columns`
 *   del dataset seleccionado quedan deshabilitados.
 * - Si el schema lleva >30 días sin refrescar, banner ámbar con botón "Refrescar".
 * - Resultados con costo (request_units + latency_ms) + items mapeados.
 */
export default function SecopLiveSearchPanel() {
  const [open, setOpen] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [keywords, setKeywords] = useState("");
  const [valorMin, setValorMin] = useState("");
  const [valorMax, setValorMax] = useState("");
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [refreshingSchema, setRefreshingSchema] = useState(false);
  const [result, setResult] = useState(null);

  const loadDatasets = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/secop/datasets");
      const enabled = (data.items || []).filter((d) => d.enabled);
      setDatasets(enabled);
      if (!selectedId && enabled.length > 0) setSelectedId(enabled[0].id);
    } catch (e) {
      // silencioso — el panel funciona si hay al menos un dataset
    }
  }, [selectedId]);

  useEffect(() => { if (open) loadDatasets(); }, [open, loadDatasets]);

  const selected = useMemo(
    () => datasets.find((d) => d.id === selectedId) || null,
    [datasets, selectedId],
  );

  // Compute schema age fresh each render — Date.now() is impure so we can't memoize it.
  const schemaAgeDays = (() => {
    if (!selected?.schema_refreshed_at) return null;
    const ms = Date.now() - new Date(selected.schema_refreshed_at).getTime();
    return Math.floor(ms / 86400000);
  })();

  const schemaStale = !selected?.schema_refreshed_at || (schemaAgeDays != null && schemaAgeDays > SCHEMA_FRESH_DAYS);

  // Set de field_names disponibles en el dataset seleccionado
  const availableFields = useMemo(() => {
    const cols = selected?.available_columns || [];
    return new Set(cols.map((c) => c.field_name));
  }, [selected]);

  const isParamEnabled = useCallback((p) => {
    if (!p.required_field) return true; // q siempre habilitado
    // Para valor_min/max, requerimos que el value_field del dataset esté en availableFields
    const vf = selected?.value_field;
    if (!vf) return false;
    // Si el schema no se ha refrescado nunca, asumimos habilitado (best effort)
    if (availableFields.size === 0) return true;
    return availableFields.has(vf);
  }, [selected, availableFields]);

  const refreshSchema = useCallback(async () => {
    if (!selected) return;
    setRefreshingSchema(true);
    try {
      const { data } = await api.post(`/admin/secop/datasets/${selected.id}/refresh-schema`);
      if (data.error) {
        toast.error(`No se pudo refrescar schema: ${data.error}`);
      } else {
        toast.success(`Schema refrescado: ${data.columns_count} columnas`);
      }
      await loadDatasets();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al refrescar schema");
    } finally {
      setRefreshingSchema(false);
    }
  }, [selected, loadDatasets]);

  const runSearch = useCallback(async () => {
    if (!selected) {
      toast.error("Selecciona un dataset");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const body = {
        dataset_uuid: selected.id,
        keywords: keywords.trim(),
        valor_min: valorMin ? Number(valorMin) : null,
        valor_max: valorMax ? Number(valorMax) : null,
        limit: Number(limit) || 20,
      };
      const { data } = await api.post("/admin/secop/search-live", body);
      setResult(data);
      if (data.count === 0) {
        toast.info("Sin resultados para esa consulta");
      } else {
        toast.success(`${data.count} resultado(s) · ${data.query_cost?.latency_ms ?? "?"}ms`);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error en la búsqueda");
    } finally {
      setLoading(false);
    }
  }, [selected, keywords, valorMin, valorMax, limit]);

  return (
    <div className="border border-gray-200 bg-white" data-testid="secop-live-search-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between gap-2 hover:bg-gray-50"
        data-testid="secop-live-search-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Radio size={14} className="text-rose-500" />
          <h2 className="font-serif text-base">Buscar en SECOP en vivo</h2>
          <span className="text-[10px] uppercase tracking-widest text-gray-500">
            consulta directa Socrata
          </span>
        </div>
        <span className="text-[11px] text-gray-500">
          {datasets.length} dataset(s) habilitados
        </span>
      </button>

      {open && (
        <div className="border-t border-gray-200" data-testid="secop-live-search-body">
          {/* Schema stale warning */}
          {selected && schemaStale && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-800 flex items-center justify-between gap-2" data-testid="secop-schema-stale-warning">
              <span className="flex items-center gap-2">
                <AlertTriangle size={12} />
                Schema {schemaAgeDays != null ? `desactualizado (${schemaAgeDays}d)` : "no refrescado"} —
                algunos parámetros podrían estar mal habilitados.
              </span>
              <Button
                type="button"
                onClick={refreshSchema}
                disabled={refreshingSchema}
                variant="outline"
                className="rounded-sm h-7 text-[11px]"
                data-testid="secop-refresh-schema-btn"
              >
                {refreshingSchema
                  ? <Loader2 size={11} className="animate-spin mr-1" />
                  : <RefreshCw size={11} className="mr-1" />}
                Refrescar schema
              </Button>
            </div>
          )}

          {/* Form */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3 text-xs">
            <div className="md:col-span-5">
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Dataset</label>
              <select
                value={selectedId}
                onChange={(e) => { setSelectedId(e.target.value); setResult(null); }}
                className="w-full h-9 px-2 text-xs border border-gray-300 rounded-sm bg-white"
                data-testid="secop-live-dataset-select"
              >
                {datasets.length === 0 && <option value="">— sin datasets habilitados —</option>}
                {datasets.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.source} · {d.label} ({d.dataset_id})
                  </option>
                ))}
              </select>
              {selected && (
                <p className="text-[10px] text-gray-500 mt-1 italic">{selected.description}</p>
              )}
            </div>
            <div className="md:col-span-4">
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Búsqueda libre (q)</label>
              <Input
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                placeholder="palabras clave (objeto, entidad…)"
                className="h-9 text-xs rounded-sm"
                data-testid="secop-live-keywords-input"
              />
            </div>
            <div className="md:col-span-3 flex items-end">
              <Button
                type="button"
                onClick={runSearch}
                disabled={loading || !selected}
                className="rounded-sm h-9 text-xs w-full"
                data-testid="secop-live-search-btn"
              >
                {loading ? <Loader2 size={13} className="animate-spin mr-1" /> : <Search size={13} className="mr-1" />}
                Buscar
              </Button>
            </div>

            {SUPPORTED_PARAMS.filter((p) => p.id !== "q").map((p) => {
              const enabled = isParamEnabled(p);
              const isMin = p.id === "valor_min";
              return (
                <div key={p.id} className="md:col-span-3">
                  <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1">
                    {p.label}
                    {!enabled && (
                      <span className="text-[9px] text-rose-600 normal-case" title="Campo no disponible en este dataset">
                        (no soportado)
                      </span>
                    )}
                  </label>
                  <Input
                    type={p.type}
                    value={isMin ? valorMin : valorMax}
                    onChange={(e) => isMin ? setValorMin(e.target.value) : setValorMax(e.target.value)}
                    disabled={!enabled}
                    placeholder={p.placeholder}
                    className="h-8 text-xs rounded-sm font-mono"
                    data-testid={`secop-live-input-${p.id}`}
                  />
                </div>
              );
            })}
            <div className="md:col-span-3">
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Límite (1–50)</label>
              <Input
                type="number"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                min={1} max={50}
                className="h-8 text-xs rounded-sm font-mono"
                data-testid="secop-live-limit-input"
              />
            </div>
          </div>

          {/* Resultado */}
          {result && (
            <div className="border-t border-gray-200" data-testid="secop-live-search-result">
              <div className="px-4 py-2 bg-gray-50 text-[11px] text-gray-700 flex items-center justify-between flex-wrap gap-2">
                <span className="flex items-center gap-2">
                  <Zap size={12} className="text-amber-600" />
                  <strong>{result.count}</strong> resultado(s) ·
                  costo: <strong>{result.query_cost?.request_units ?? 1}</strong> unidad(es) ·
                  latencia: <strong>{result.query_cost?.latency_ms ?? "?"}</strong>ms
                  {result.from_cache && (
                    <span className="px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded-sm">desde caché</span>
                  )}
                </span>
                <span className="text-gray-500 text-[10px] font-mono">
                  dataset_id: {result.dataset?.dataset_id}
                </span>
              </div>
              {result.items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Objeto</th>
                        <th className="px-3 py-2 text-left">Entidad</th>
                        <th className="px-3 py-2 text-right">Valor</th>
                        <th className="px-3 py-2 text-left">Fecha</th>
                        <th className="px-3 py-2 text-right">Metadata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.items.map((it, i) => (
                        <LiveResultRow key={i} item={it} index={i} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LiveResultRow({ item, index }) {
  const [showMeta, setShowMeta] = useState(false);
  const rawCount = item.raw_metadata ? Object.keys(item.raw_metadata).length : 0;
  return (
    <>
      <tr className="border-t border-gray-100 hover:bg-gray-50" data-testid={`secop-live-result-row-${index}`}>
        <td className="px-3 py-2 max-w-[400px]">
          <div className="text-gray-800 truncate" title={item.objeto}>{item.objeto}</div>
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank" rel="noreferrer"
              className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5"
            >
              ver en SECOP <ExternalLink size={9} />
            </a>
          )}
        </td>
        <td className="px-3 py-2 text-gray-700 max-w-[200px] truncate" title={item.entidad}>{item.entidad || "—"}</td>
        <td className="px-3 py-2 text-right text-gray-700 font-mono tabular-nums">{formatCOP(item.valor_cop) || "—"}</td>
        <td className="px-3 py-2 text-gray-500">{item.fecha || "—"}</td>
        <td className="px-3 py-2 text-right">
          <button
            type="button"
            onClick={() => setShowMeta((v) => !v)}
            className="text-[10px] text-gray-500 hover:text-primary inline-flex items-center gap-1"
            data-testid={`secop-live-meta-toggle-${index}`}
          >
            <ClipboardList size={11} />
            {rawCount} campo(s)
          </button>
        </td>
      </tr>
      {showMeta && (
        <tr className="bg-gray-50/50" data-testid={`secop-live-meta-row-${index}`}>
          <td colSpan={5} className="px-4 py-3">
            <pre className="text-[10px] font-mono whitespace-pre-wrap break-all max-h-64 overflow-y-auto bg-white border border-gray-200 p-2 rounded-sm">
              {JSON.stringify(item.raw_metadata, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}
